import { Head, Link } from "@inertiajs/react"
import { useMemo } from "react"
import { Printer } from "lucide-react"

import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { type CutPlans, type WireRoom, roomsFromServer } from "@/editor-core/document"
import { productLabel } from "@/editor-core/rollup"
import {
  type ProductAssignment,
  type Roll,
  assignCuts,
  deriveCuts,
  rollGood,
  toSeamSettings,
} from "@/editor-core/seams"
import type { AdminSettings } from "@/types/inertia"

// The printable cut sheet: numbered cuts on rolls per roll-goods product,
// remnants split out from waste — the piece of paper the warehouse cuts
// from. Read-only: seams and cut placement are edited in the takeoff editor.
//
// Cuts are DERIVED here by the same editor-core modules the editor renders
// from, so this page and the editor's cut plan can never disagree. Widths
// print per cut; the product line prints the NOMINAL roll width ("nominal
// for print, usable for math").

type Props = {
  takeoff: {
    id: number
    name: string
    url: string
    owner: { kind: "plan" | "project"; id: number; display_name: string; url: string }
  }
  rooms: WireRoom[]
  cut_plans: CutPlans
  settings: AdminSettings
  calibrations: { sheet_id: number; label: string; ft_per_px: number | null }[]
}

export default function CutSheetPage(props: Props) {
  const { takeoff } = props

  const products = useMemo(() => {
    const settings = toSeamSettings(props.settings)
    const ftBySheet = new Map(props.calibrations.map((c) => [c.sheet_id, c.ft_per_px]))
    const byProduct = new Map<
      string,
      { product: NonNullable<WireRoom["product"]>; cuts: ReturnType<typeof deriveCuts> }
    >()
    for (const room of roomsFromServer(props.rooms)) {
      if (!room.seams || !room.product || !rollGood(room.product)) continue
      const ftPerPx = ftBySheet.get(room.sheetId)
      if (!ftPerPx) continue
      const cuts = deriveCuts(
        {
          id: room.id,
          name: room.name,
          outline: room.outline,
          holes: room.holes,
          multiplier: room.multiplier,
          seams: room.seams,
        },
        ftPerPx,
        settings
      )
      const entry = byProduct.get(room.product.id)
      if (entry) entry.cuts.push(...cuts)
      else byProduct.set(room.product.id, { product: room.product, cuts: [...cuts] })
    }
    return [...byProduct.values()]
      .map(({ product, cuts }) => ({
        product,
        assignment: assignCuts(cuts, props.cut_plans[product.id], product, settings),
      }))
      .sort((a, b) => productLabel(a.product).localeCompare(productLabel(b.product)))
  }, [props.rooms, props.cut_plans, props.settings, props.calibrations])

  const rollLengthFt = props.settings.roll_length_ft
  const title = `Cut Sheet — ${takeoff.owner.display_name}`
  const description =
    "Numbered cuts on rolls for every roll-goods product on this takeoff, with remnants tracked separately from waste — what the warehouse cuts from."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="print-chrome mb-4 flex flex-wrap items-center gap-3">
          <Link href={takeoff.url} className="no-underline hover:underline">
            ← {takeoff.name}
          </Link>
          <span className="ml-auto flex gap-2">
            <Button variant="soft" onClick={() => window.print()} data-testid="print-cut-sheet">
              <Printer className="mr-1.5 h-4 w-4" />
              Print / Save as PDF
            </Button>
          </span>
        </div>

        <article className="print-sheet mx-auto max-w-5xl rounded-lg border border-hairline bg-surface p-6">
          <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline pb-3">
            <h1>Cut Sheet</h1>
            <p className="text-ink-display">
              {takeoff.owner.display_name}
              <span className="text-ink-muted"> · {takeoff.name}</span>
            </p>
          </header>

          {products.length === 0 ? (
            <p className="mt-6 text-ink-muted">
              No roll-goods cut plans yet — assign a carpet or sheet vinyl product to a room and
              plan its seams in the editor.
            </p>
          ) : (
            products.map(({ product, assignment }) => (
              <section key={product.id} className="mt-6" data-testid={`cut-sheet-${product.id}`}>
                <h2>
                  {productLabel(product)}
                  {product.roll_width_ft != null && (
                    <span className="text-ink-muted"> — {product.roll_width_ft}&prime; roll</span>
                  )}
                </h2>
                <p className="mt-0.5 text-sm text-ink-muted">
                  {assignment.totalLf.toFixed(1)} lf across {assignment.rolls.length}{" "}
                  {assignment.rolls.length === 1 ? "roll" : "rolls"} · {assignment.cutCount} cuts
                  {product.repeat_len_in != null &&
                    ` · ${product.repeat_len_in}″ repeat${product.half_drop ? ", half drop" : ""}`}
                </p>

                {assignment.rolls.map((roll) => (
                  <RollDiagram key={roll.index} roll={roll} rollLengthFt={rollLengthFt} />
                ))}

                <RemnantSummary assignment={assignment} />
              </section>
            ))
          )}

          <footer className="mt-6 border-t border-hairline pt-2 text-xs text-ink-muted">
            {takeoff.owner.display_name} · {takeoff.name} · roll length {rollLengthFt}&prime;
          </footer>
        </article>
      </AppShell>
    </>
  )
}

// One roll: a 2D diagram at true width/length ratio — cuts sit exactly
// where the allocation (and the editor's tray) placed them, narrow fills
// stacked side-by-side across the width. Then the cut table beneath it.
function RollDiagram({ roll, rollLengthFt }: { roll: Roll; rollLengthFt: number }) {
  const total = Math.max(rollLengthFt, roll.usedFt) * 1.01
  const width = Math.max(roll.cuts.reduce((m, c) => Math.max(m, c.yFt + c.widthFt), 0), 12)
  return (
    <div className="mt-3 break-inside-avoid" data-testid={`cut-sheet-roll-${roll.index}`}>
      <p className="flex items-baseline gap-2 text-sm">
        <strong>Roll {roll.index + 1}</strong>
        <span className="ml-auto font-mono text-xs tabular-nums text-ink-muted">
          {roll.usedFt.toFixed(1)} lf used
          {roll.leftoverKind === "remnant" && ` · remnant ${roll.leftoverFt.toFixed(1)} lf`}
          {roll.leftoverKind === "waste" && ` · waste ${roll.leftoverFt.toFixed(1)} lf`}
          {roll.overflow && " · OVER LENGTH"}
        </span>
      </p>
      <svg
        viewBox={`0 0 ${total} ${width}`}
        preserveAspectRatio="none"
        className="mt-1 w-full rounded border border-hairline"
        style={{ aspectRatio: `${total} / ${Math.max(width * 2.2, 14)}` }}
        role="img"
        aria-label={`Roll ${roll.index + 1} cut layout`}
      >
        {roll.leftoverFt > 0 && (
          <rect
            x={roll.usedFt}
            y={0}
            width={Math.max(rollLengthFt - roll.usedFt, 0)}
            height={width}
            fill={
              roll.leftoverKind === "remnant" ? "rgba(22,163,74,0.12)" : "rgba(120,113,108,0.12)"
            }
            stroke="rgba(120,113,108,0.4)"
            strokeWidth={0.08}
            strokeDasharray="0.8 0.8"
          />
        )}
        <line
          x1={rollLengthFt}
          y1={0}
          x2={rollLengthFt}
          y2={width}
          stroke="rgba(220,38,38,0.6)"
          strokeWidth={0.12}
          strokeDasharray="0.6 0.6"
        />
        {roll.cuts.map((cut) => (
          <g key={cut.key}>
            <title>
              {`#${cut.number} · ${cut.roomName}${cut.copy > 0 ? ` (copy ${cut.copy + 1})` : ""} · ${cut.widthFt.toFixed(2)}′ × ${cut.lengthFt.toFixed(2)}′`}
            </title>
            {cut.repeatFt > 0 && (
              <rect
                x={cut.xFt - cut.repeatFt}
                y={cut.yFt}
                width={cut.repeatFt}
                height={cut.widthFt}
                fill="rgba(124,58,237,0.12)"
              />
            )}
            {/* Material off the roll (borderless faint fill) with the true
                piece drawn on top — an L-strip prints as its real L, the
                rest of the rect is claimable empty space. Only the piece
                gets a hairline so the printed shapes stay crisp. */}
            <rect
              x={cut.xFt}
              y={cut.yFt}
              width={cut.lengthFt}
              height={cut.widthFt}
              fill={cut.overrun || cut.overlap ? "rgba(220,38,38,0.15)" : "rgba(37,99,235,0.07)"}
            />
            {cut.shape.length >= 3 && (
              <polygon
                points={cut.shape.map(([px, py]) => `${px},${py}`).join(" ")}
                transform={`translate(${cut.xFt} ${cut.yFt})`}
                fill={
                  cut.overrun || cut.overlap
                    ? "rgba(220,38,38,0.32)"
                    : cut.narrowFill
                      ? "rgba(220,38,38,0.16)"
                      : "rgba(37,99,235,0.22)"
                }
                stroke="rgba(30,64,175,0.5)"
                strokeWidth={0.08}
              />
            )}
            <text
              x={cut.xFt + cut.lengthFt / 2}
              y={cut.yFt + cut.widthFt / 2 + 1}
              textAnchor="middle"
              fontSize={Math.min(2.6, cut.widthFt * 0.75)}
              fill="currentColor"
            >
              {cut.number}
            </text>
          </g>
        ))}
      </svg>
      {/* House rule: wide tables scroll in their own container, the page
          body never scrolls horizontally. On phones with wide fallback
          fonts the cuts table exceeds the viewport (caught by CI's Linux
          Chrome at 390px — DejaVu renders wider than macOS system fonts);
          print keeps overflow visible so paper never clips columns. */}
      <div className="overflow-x-auto print:overflow-visible">
      <table className="mt-1 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-ink-muted text-left text-xs uppercase tracking-wide text-ink-muted">
            <th className="px-1.5 pb-1 font-semibold">Cut</th>
            <th className="px-1.5 pb-1 font-semibold">Room</th>
            <th className="px-1.5 pb-1 text-right font-semibold">Width</th>
            <th className="px-1.5 pb-1 text-right font-semibold">Length</th>
            <th className="px-1.5 pb-1 text-right font-semibold">Repeat</th>
            <th className="px-1.5 pb-1 font-semibold">Flags</th>
          </tr>
        </thead>
        <tbody>
          {roll.cuts.map((cut) => (
            <tr key={cut.key} className="schedule-row border-b border-hairline">
              <td className="px-1.5 py-1 font-mono font-semibold tabular-nums">#{cut.number}</td>
              <td className="px-1.5 py-1">
                {cut.roomName}
                {cut.copy > 0 && <span className="text-ink-muted"> (copy {cut.copy + 1})</span>}
              </td>
              <td className="px-1.5 py-1 text-right font-mono tabular-nums">
                {cut.widthFt.toFixed(2)}&prime;
              </td>
              <td className="px-1.5 py-1 text-right font-mono tabular-nums">
                {cut.lengthFt.toFixed(2)}&prime;
              </td>
              <td className="px-1.5 py-1 text-right font-mono tabular-nums">
                {cut.repeatFt > 0 ? `+${cut.repeatFt.toFixed(2)}′` : "—"}
              </td>
              <td className="px-1.5 py-1 text-xs text-ink-muted">
                {[
                  cut.narrowFill && "narrow fill",
                  cut.pinned && "placed by hand",
                  cut.overrun && "OVERRUN",
                  cut.overlap && "OVERLAP",
                ]
                  .filter(Boolean)
                  .join(" · ") || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

// Remnants are reusable stock and print as their own list — the whole point
// of tracking them separately from waste.
function RemnantSummary({ assignment }: { assignment: ProductAssignment }) {
  const remnants = assignment.rolls.filter((roll) => roll.leftoverKind === "remnant")
  const wasteFt = assignment.rolls
    .filter((roll) => roll.leftoverKind === "waste")
    .reduce((sum, roll) => sum + roll.leftoverFt, 0)
  if (remnants.length === 0 && wasteFt === 0) return null
  return (
    <p className="mt-2 text-sm" data-testid="remnant-summary">
      {remnants.length > 0 && (
        <>
          <strong>Remnants:</strong>{" "}
          {remnants
            .map((roll) => `${roll.leftoverFt.toFixed(1)} lf (roll ${roll.index + 1})`)
            .join(", ")}
        </>
      )}
      {wasteFt > 0 && (
        <span className="text-ink-muted">
          {remnants.length > 0 && " · "}end waste {wasteFt.toFixed(1)} lf
        </span>
      )}
    </p>
  )
}
